import React, { useState, useEffect, useRef } from "react";

import { useNavigate } from "react-router-dom";

const WebPlayerSpeedTest = () => {
  const navigate = useNavigate();
  const [speed, setSpeed] = useState(0);
  const [displaySpeed, setDisplaySpeed] = useState(0); // Visual needle position
  const [ping, setPing] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [testPhase, setTestPhase] = useState("");
  const [finalComment, setFinalComment] = useState("");
  const [funnyMessage, setFunnyMessage] = useState("");
  const abortControllerRef = useRef(null);
  const animationFrameRef = useRef(null);
  const messageIntervalRef = useRef(null);

  // Spring physics for fluid needle animation
  const needlePosition = useRef(0);
  const needleVelocity = useRef(0);
  const targetSpeed = useRef(0);

  // Spring physics parameters for ultra-smooth movement
  const SPRING_STIFFNESS = 0.5; // Extremely tight coupling to eliminate lag
  const SPRING_DAMPING = 0.8; // Higher damping for immediate response
  const VELOCITY_THRESHOLD = 0.001;
  const POSITION_THRESHOLD = 0.1;

  // Calculate needle rotation angle (0 to 180 degrees)
  const getNeedleAngle = (value) => {
    const maxSpeed = 1000;
    // Ensure we have a valid number and clamp it
    const speedValue = Math.min(1000, Math.max(0, Number(value) || 0));
    const angle = (speedValue / maxSpeed) * 180;

    return angle; // Already clamped by speedValue
  };

  // Spring physics animation for ultra-smooth needle movement
  useEffect(() => {
    targetSpeed.current = speed;
  }, [speed]);

  useEffect(() => {
    let animationId;

    const animate = () => {
      const target = targetSpeed.current;
      const position = needlePosition.current;
      const velocity = needleVelocity.current;

      // Calculate spring force (Hooke's law)
      const displacement = target - position;
      const springForce = displacement * SPRING_STIFFNESS;

      // Update velocity with spring force and damping
      const newVelocity = (velocity + springForce) * SPRING_DAMPING;

      needleVelocity.current = newVelocity;

      // Update position based on velocity (clamp to valid range)
      // Add a small amount of the displacement directly for tighter tracking
      const directCorrection = displacement * 0.15; // Direct correction factor
      const newPosition = Math.min(
        1000,
        Math.max(0, position + newVelocity + directCorrection)
      );

      needlePosition.current = newPosition;

      // Always update display with clamped position
      setDisplaySpeed(newPosition);

      // Check if animation should continue
      const isMoving =
        Math.abs(newVelocity) > VELOCITY_THRESHOLD ||
        Math.abs(displacement) > POSITION_THRESHOLD;

      if (isMoving) {
        animationId = requestAnimationFrame(animate);
      } else {
        // Snap to final position when movement is minimal (with clamping)
        const clampedTarget = Math.min(1000, Math.max(0, target));

        needlePosition.current = clampedTarget;
        setDisplaySpeed(clampedTarget);
      }
    };

    // Start animation loop
    animationId = requestAnimationFrame(animate);

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [speed]);

  // Generate random funny messages during testing
  const getFunnyTestingMessage = () => {
    const messages = [
      "Waking up the hamsters... 🐹",
      "Unleashing the internet gremlins... 👹",
      "Counting the bits... 🔢",
      "Turbo mode engaging... 🚀",
      "Summoning bandwidth spirits... 👻",
      "Caffeinating the servers... ☕",
      "Downloading more RAM... 💾",
      "Feeding the fiber optics... 🍝",
      "Polishing the copper wires... ✨",
      "Bribing the ISP... 💰",
      "Yelling at the router... 📢",
      "Performing digital magic... 🎩",
      "Chasing electrons... ⚡",
      "Flexing internet muscles... 💪",
      "Spinning up flux capacitor... ⚙️",
      "Consulting the speed gods... 🙏",
      "Revving the data engine... 🏎️",
      "Tickling the packets... 🎯",
      "Herding the data cats... 🐱",
      "Optimizing the tubes... 🌐",
    ];

    return messages[Math.floor(Math.random() * messages.length)];
  };

  // Generate random speed comment based on speed
  const generateSpeedComment = (speed) => {
    const low = [
      "Turtle Power 🐢💨",
      "Are We On Dial-up? 📞💻",
      "Powered By Potato 🥔⚡",
    ];
    const mid = [
      "So Much Wow 😲",
      "Mediocre But Mighty 💪📶",
      "Not Too Shabby 😎",
    ];
    const high = [
      "Zoom Zoom, Baby 🏎️💨",
      "Speed Demon 😈",
      "Warp Speed Engaged 🚀",
    ];
    const extreme = ["Mach 10 Madness 💥", "Too Fast 🏎️💨", "Speedy AF 🏎️💨"];

    if (speed < 100) {
      return low[Math.floor(Math.random() * low.length)];
    } else if (speed < 400) {
      return mid[Math.floor(Math.random() * mid.length)];
    } else if (speed > 750) {
      return extreme[Math.floor(Math.random() * extreme.length)];
    } else {
      return high[Math.floor(Math.random() * high.length)];
    }
  };

  // Measure ping/jitter
  const measurePing = async () => {
    const pingServer = "https://lunova.s3-accelerate.amazonaws.com/100MB.bin";
    const maxAttempts = 5;
    const pings = [];

    const runPingAttempt = async (attempt) => {
      try {
        const startTime = performance.now();
        const response = await fetch(pingServer, {
          method: "HEAD",
          mode: "cors",
          cache: "no-cache",
        });

        if (response.ok) {
          const endTime = performance.now();
          const pingTime = Math.round(endTime - startTime);

          pings.push(pingTime);
        }
      } catch (error) {
        console.error("Ping attempt failed:", error);
      }

      if (attempt + 1 < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, 100));

        return runPingAttempt(attempt + 1);
      }

      return undefined;
    };

    await runPingAttempt(0);

    // Calculate average ping (jitter)
    const avgPing =
      pings.length > 0
        ? Math.round(pings.reduce((a, b) => a + b, 0) / pings.length)
        : 0;

    return avgPing;
  };

  // Run download speed test
  const runDownloadTest = async () => {
    const downloadServer = "https://lunova.s3-accelerate.amazonaws.com/1GB.bin";
    const testDuration = 10000; // 10 seconds
    const numConnections = 4; // Use 4 parallel connections
    const startTime = Date.now();
    let totalBytes = 0;

    // Create abort controller for this test
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    // Reset speed and physics at start
    setSpeed(0);
    needlePosition.current = 0;
    needleVelocity.current = 0;
    targetSpeed.current = 0;

    // Update speed display more frequently for better sync
    const updateSpeed = () => {
      const elapsedSeconds = (Date.now() - startTime) / 1000;

      if (elapsedSeconds > 0.1) {
        // Start updating after 100ms
        const mbps = (totalBytes * 8) / (elapsedSeconds * 1000000);
        const clampedSpeed = Math.min(mbps, 1000);

        // Update target speed directly for spring physics to handle
        setSpeed(clampedSpeed);
      }

      if (Date.now() - startTime < testDuration && !signal.aborted) {
        animationFrameRef.current = requestAnimationFrame(updateSpeed);
      }
    };

    // Start updating immediately
    updateSpeed();

    const readDownload = async (reader) => {
      if (Date.now() - startTime >= testDuration || signal.aborted) return;

      const { done, value } = await reader.read();

      if (done) return;

      totalBytes += value.length;

      return readDownload(reader);
    };

    const runDownloadConnection = async () => {
      if (Date.now() - startTime >= testDuration || signal.aborted) return;

      try {
        const response = await fetch(downloadServer, {
          signal,
          cache: "no-cache",
        });

        if (!response.ok) throw new Error("Network response not ok");

        const reader = response.body.getReader();

        await readDownload(reader);
        await reader.cancel();

        return runDownloadConnection();
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Download thread failed:", error);
        }
      }
    };

    // Start parallel downloads.
    const downloadPromises = Array.from({ length: numConnections }, () =>
      runDownloadConnection()
    );

    // Wait for test duration
    await Promise.race([
      Promise.all(downloadPromises),
      new Promise((resolve) => setTimeout(resolve, testDuration)),
    ]);

    // Cancel any ongoing requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Calculate final speed
    const elapsedSeconds = (Date.now() - startTime) / 1000;
    const finalSpeed = (totalBytes * 8) / (elapsedSeconds * 1000000);
    const clampedSpeed = Math.min(finalSpeed, 1000);

    return clampedSpeed;
  };

  // Start speed test
  const startTest = async () => {
    if (isRunning) return;

    setIsRunning(true);
    setSpeed(0);
    setDisplaySpeed(0); // Reset visual needle position
    needlePosition.current = 0;
    needleVelocity.current = 0;
    targetSpeed.current = 0;
    setPing(0);
    setFinalComment(""); // Clear previous comment
    setTestPhase("Measuring latency...");

    try {
      // Measure ping first
      const pingResult = await measurePing();

      setPing(pingResult);

      // Update phase
      setTestPhase("Testing download speed...");

      // Start cycling funny messages
      setFunnyMessage(getFunnyTestingMessage());
      messageIntervalRef.current = setInterval(() => {
        setFunnyMessage(getFunnyTestingMessage());
      }, 2000); // Change message every 2 seconds

      // Run download test
      const downloadSpeed = await runDownloadTest();

      // Clear message interval
      if (messageIntervalRef.current) {
        clearInterval(messageIntervalRef.current);
        messageIntervalRef.current = null;
      }
      setFunnyMessage("");

      // Final animation to settle the needle
      setTestPhase("Finalizing...");

      // Simply set the final speed and let spring physics handle the animation
      setSpeed(downloadSpeed);

      // Wait a moment for the needle to settle, then show "Done"
      setTimeout(() => {
        setTestPhase("Done");
        // Generate and set the final comment
        const comment = generateSpeedComment(downloadSpeed);

        setFinalComment(comment);
      }, 1500);

      // Save result to localStorage
      const results = JSON.parse(
        localStorage.getItem("speedTestHistory") || "[]"
      );

      results.push({
        ping: pingResult,
        download: downloadSpeed,
        timestamp: Date.now(),
      });
      localStorage.setItem(
        "speedTestHistory",
        JSON.stringify(results.slice(-10))
      );
    } catch (error) {
      console.error("Speed test failed:", error);
      setTestPhase("Error");
    } finally {
      setTimeout(() => {
        setIsRunning(false);
      }, 500);
    }
  };

  // Cleanup on unmount
  useEffect(
    () => () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (messageIntervalRef.current) {
        clearInterval(messageIntervalRef.current);
      }
    },
    []
  );

  // Calculate needle angle - use displaySpeed for smooth animation
  const needleAngle = getNeedleAngle(displaySpeed);

  return (
    <>
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
      `}</style>
      <div className="min-h-screen relative flex items-center justify-center overflow-hidden">
        {/* Dark overlay with 0.5 opacity */}
        <div className="fixed inset-0 bg-black/50 z-0" />

        {/* Back button */}
        <button
          className="fixed top-6 left-6 z-50 text-white/60 hover:text-white transition-all duration-300"
          onClick={() => navigate(-1)}
        >
          <i className="fas fa-arrow-left text-2xl" />
        </button>

        {/* Main speedometer container */}
        <div className="relative z-10 w-full max-w-2xl px-8">
          {/* Ping display at top */}
          <div className="text-center mb-8">
            <div className="text-white/60 text-sm font-light tracking-wider">
              {ping > 0 ? `${ping} ms` : "0 ms"}
            </div>
          </div>

          {/* Speedometer SVG */}
          <div className="relative">
            <svg
              className="w-full h-auto"
              style={{ maxWidth: "600px", margin: "0 auto" }}
              viewBox="0 0 400 250"
            >
              {/* Define gradient */}
              <defs>
                <linearGradient
                  id="speedGradient"
                  x1="0%"
                  x2="100%"
                  y1="0%"
                  y2="0%"
                >
                  <stop offset="0%" stopColor="#60a5fa" /> {/* Light blue */}
                  <stop offset="100%" stopColor="#10b981" /> {/* Green */}
                </linearGradient>
                <filter id="glow">
                  <feGaussianBlur result="coloredBlur" stdDeviation="3" />
                  <feMerge>
                    <feMergeNode in="coloredBlur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              {/* Background arc */}
              <path
                d="M 50 200 A 150 150 0 0 1 350 200"
                fill="none"
                stroke="rgba(255,255,255,0.1)"
                strokeWidth="2"
              />

              {/* Colored progress arc - follows needle in real-time */}
              <path
                d="M 50 200 A 150 150 0 0 1 350 200"
                fill="none"
                stroke="url(#speedGradient)"
                strokeDasharray={`${(needleAngle / 180) * 471.24} 471.24`}
                strokeWidth="3"
                style={{
                  filter: "url(#glow)",
                }}
              />

              {/* Speed markers - properly spaced */}
              {[0, 100, 200, 300, 400, 500, 600, 700, 800, 900, 1000].map(
                (value) => {
                  // Calculate angle based on actual value (0-1000 maps to 0-180 degrees)
                  const angle = (value / 1000) * 180;
                  const radian = (angle * Math.PI) / 180;
                  const x1 = 200 - 140 * Math.cos(radian);
                  const y1 = 200 - 140 * Math.sin(radian);
                  const x2 = 200 - 130 * Math.cos(radian);
                  const y2 = 200 - 130 * Math.sin(radian);
                  const textX = 200 - 115 * Math.cos(radian);
                  const textY = 200 - 115 * Math.sin(radian);

                  return (
                    <g key={value}>
                      <line
                        stroke="rgba(255,255,255,0.3)"
                        strokeWidth="1"
                        x1={x1}
                        x2={x2}
                        y1={y1}
                        y2={y2}
                      />
                      <text
                        className="font-light"
                        dominantBaseline="middle"
                        fill="#60a5fa"
                        fontSize="12"
                        textAnchor="middle"
                        x={textX}
                        y={textY}
                      >
                        {value}
                      </text>
                    </g>
                  );
                }
              )}

              {/* Minor tick marks - every 20 Mbps */}
              {Array.from({ length: 51 }).map((_, i) => {
                const value = i * 20; // Every 20 Mbps

                // Skip major marks (multiples of 100)
                if (value % 100 === 0) return null;

                const angle = (value / 1000) * 180;
                const radian = (angle * Math.PI) / 180;
                const x1 = 200 - 140 * Math.cos(radian);
                const y1 = 200 - 140 * Math.sin(radian);
                const x2 = 200 - 135 * Math.cos(radian);
                const y2 = 200 - 135 * Math.sin(radian);

                return (
                  <line
                    key={`minor-${i}`}
                    stroke="rgba(255,255,255,0.15)"
                    strokeWidth="0.5"
                    x1={x1}
                    x2={x2}
                    y1={y1}
                    y2={y2}
                  />
                );
              })}

              {/* Needle - Smooth animation via requestAnimationFrame */}
              <g transform={`rotate(${needleAngle} 200 200)`}>
                <line
                  stroke="white"
                  strokeLinecap="round"
                  strokeWidth="2"
                  style={{
                    filter: "drop-shadow(0 0 5px rgba(255,255,255,0.5))",
                  }}
                  x1="200"
                  x2="60"
                  y1="200"
                  y2="200"
                />
                <circle cx="200" cy="200" fill="white" r="4" />
              </g>

              {/* Center text - shows actual needle position */}
              <text
                className="font-mono"
                fill="white"
                fontSize="48"
                fontWeight="300"
                textAnchor="middle"
                x="200"
                y="170"
              >
                {Math.max(0, displaySpeed).toFixed(1)}
              </text>
              <text
                className="font-light"
                fill="rgba(255,255,255,0.6)"
                fontSize="14"
                textAnchor="middle"
                x="200"
                y="190"
              >
                Mbps
              </text>

              {/* Testing download speed text - below Mbps */}
              {testPhase === "Testing download speed..." && funnyMessage && (
                <text
                  className="font-light"
                  fill="rgba(255,255,255,0.5)"
                  fontSize="12"
                  textAnchor="middle"
                  x="200"
                  y="230"
                >
                  {funnyMessage}
                </text>
              )}

              {/* Status text */}
              {testPhase && testPhase !== "Testing download speed..." && (
                <text
                  className="font-light"
                  fill="rgba(255,255,255,0.5)"
                  fontSize="12"
                  textAnchor="middle"
                  x="200"
                  y="230"
                >
                  {testPhase}
                </text>
              )}
            </svg>
          </div>

          {/* Final comment display - Above the button */}
          <div className="h-20 flex items-center justify-center mt-8">
            {finalComment && testPhase === "Done" && (
              <div className="text-center">
                <div className="text-white text-2xl font-thin tracking-wide animate-fadeIn">
                  {finalComment}
                </div>
                <div className="text-white/50 text-sm font-thin mt-1">
                  {displaySpeed.toFixed(1)} Mbps • {ping} ms ping
                </div>
              </div>
            )}
          </div>

          {/* Check Speed button */}
          <div className="flex justify-center">
            <button
              className={`
              px-12 py-4 rounded-full font-light text-lg tracking-wider
              border-2 transition-all duration-300
              ${
                isRunning
                  ? "border-gray-600 text-gray-500 bg-transparent cursor-not-allowed"
                  : "border-purple-500 text-white bg-transparent hover:bg-purple-500/20 hover:shadow-lg hover:shadow-purple-500/30"
              }
            `}
              disabled={isRunning}
              onClick={startTest}
            >
              {isRunning ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-3" />
                  Testing...
                </>
              ) : (
                "Check Speed"
              )}
            </button>
          </div>

          {/* Status text when not done */}
          {!isRunning && displaySpeed > 0 && testPhase !== "Done" && (
            <div className="text-center mt-6 text-white/40 text-sm font-light">
              Last test: {displaySpeed.toFixed(1)} Mbps • {ping} ms ping
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default WebPlayerSpeedTest;
